/**
 * Modelos: a biblioteca de templates de WhatsApp, e-mail e voz da
 * administracao (so `platform_admin`; a rota ja e protegida em routes.tsx).
 *
 * Hierarquia, de cima para baixo:
 *  1. Faixa de identidade com "Novo modelo" (a acao primaria da pagina).
 *  2. Quatro numeros da biblioteca no escopo e na janela, com a variacao
 *     contra o periodo anterior (pagaram, taxa, responderam, biblioteca).
 *  3. "O que fazer agora" (toques parados na Meta, quedas, gargalo no
 *     comercial, o que copiar do melhor) ao lado de "Quem mais trouxe
 *     pagamento".
 *  4. A biblioteca: abas por canal, filtros combinaveis com contagem
 *     facetada, ordenacao, cartoes ou tabela, "ver mais" e a barra de
 *     comparacao lado a lado.
 *  5. Rodapes: utility x marketing, janela de 24 h, botao de resposta ->
 *     vendedora, so depois do fato; periodo e base da comparacao.
 *
 * Decisoes nao obvias:
 *  - UMA leitura (`/api/admin/templates`) traz o escopo inteiro e todo recorte
 *    de tela acontece em memoria (filtragem.ts). Refazer o fetch a cada tecla
 *    seria custo sem beneficio, e as contagens dos chips precisam do conjunto
 *    todo. A tela NUNCA renderiza a lista inteira: o "ver mais" corta.
 *  - A empresa em foco do cabecalho NAO recorta esta tela: modelos sao assunto
 *    da administracao e podem ser globais. O filtro "Empresa" e proprio e
 *    viaja na query como `companyId`.
 *  - Criar, editar, duplicar, arquivar e restaurar versao sao uma
 *    SOBREPOSICAO LOCAL sobre o que veio da API. Ela sobrevive ao refetch
 *    periodico e passa pelos mesmos filtros; some no F5, como toda interacao
 *    da previa. Arquivar e restaurar tem "Desfazer" no aviso.
 *  - A previa e a comparacao guardam o ID, nao o objeto: depois de restaurar
 *    uma versao, o modal aberto ja mostra o modelo novo.
 *  - Os modais recebem `onClose` estavel (useCallback): o Modal os coloca nas
 *    dependencias do efeito de foco, e uma arrow inline roubaria o foco do
 *    campo a cada tecla digitada no editor.
 *  - Tecla "/" leva a busca (fora de campo de texto e sem modal aberto).
 *  - "Novo modelo" so liga com a biblioteca carregada: criado durante a carga
 *    ou com erro, o modelo ia para a sobreposicao sem aparecer nem ser avisado.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState, ErrorPanel, RefreshingDot, SimulatedNotice } from '../../../components/PageState';
import { Badge, Button, Footnote, HeroBanner, Icon, Panel, Tabs, type TabItem } from '../../../components/ui';
import { AGORA_SIMULADO, useApiResource } from '../../../lib/api';
import { useCompany } from '../../../lib/companyContext';
import { formatCount, formatDate } from '../../../lib/format';
import { describeSelection } from '../../../lib/period';
import { usePeriod } from '../../../lib/periodContext';
import type { CompanySummary } from '../../../types';
import { fetchTemplates } from '../api';
import { CartaoModelo } from '../components/CartaoModelo';
import { EsqueletoModelos } from '../components/EsqueletoModelos';
import { FiltrosDeModelos } from '../components/FiltrosDeModelos';
import { ModalCompararModelos } from '../components/ModalCompararModelos';
import { ModalEditorModelo, type EditorAberto, type ModoEditor } from '../components/ModalEditorModelo';
import { ModalPreviaModelo } from '../components/ModalPreviaModelo';
import { OQueFazerModelos } from '../components/OQueFazerModelos';
import { RankingModelos } from '../components/RankingModelos';
import { ResumoBiblioteca } from '../components/ResumoBiblioteca';
import { TabelaModelos } from '../components/TabelaModelos';
import { DESEMPENHO_ZERO, ranquear } from '../desempenho';
import {
  ESCOPO_GLOBAIS,
  ESCOPO_TODAS,
  FILTROS_FINOS_ZERADOS,
  FILTROS_INICIAIS,
  ORDEM_INICIAL,
  aplicarFiltros,
  companyIdDoEscopo,
  contarFiltrosFinos,
  contarPorValor,
  contarUso,
  ordemNatural,
  ordenar,
  pertenceAoEscopo,
  type FiltrosLocais,
  type Ordem,
} from '../filtragem';
import { gerarInsights, type AbaDaPrevia, type AcaoInsight } from '../insights';
import type { MessageTemplate, TemplateTab, TemplateVersion } from '../types';
import { useAviso } from '../useAviso';
import { restaurarVersao, versaoDe } from '../versoes';
import {
  ABAS,
  ABA_ICONE,
  ABA_LABEL,
  PASSO_CARTOES,
  PASSO_TABELA,
  canalPassaPelaMeta,
  contar,
  nomeDaCopia,
  type ColunaOrdem,
  type Visualizacao,
} from '../vocabulario';

const ID_PAINEL = 'painel-modelos';
const ID_BUSCA = 'busca-modelos';
const ID_LISTA = 'lista-modelos';

/** O que a previa guardou por cima do que veio da API. */
interface Sobreposicao {
  readonly criados: readonly MessageTemplate[];
  readonly substituidos: Readonly<Record<string, MessageTemplate>>;
  readonly arquivados: readonly string[];
}

const SEM_SOBREPOSICAO: Sobreposicao = { criados: [], substituidos: {}, arquivados: [] };

/**
 * API + criados, com os substituidos por cima e sem os arquivados. O conteudo
 * editado vence; os NUMEROS continuam os da leitura mais recente, para trocar
 * o periodo no cabecalho atualizar tambem o modelo editado na previa.
 */
function mesclar(items: readonly MessageTemplate[], locais: Sobreposicao): readonly MessageTemplate[] {
  return [...items, ...locais.criados]
    .map((t) => {
      const editado = locais.substituidos[t.id];
      return editado === undefined ? t : { ...editado, performance: t.performance, previousPerformance: t.previousPerformance };
    })
    .filter((t) => !locais.arquivados.includes(t.id));
}

function passoDe(v: Visualizacao): number {
  return v === 'tabela' ? PASSO_TABELA : PASSO_CARTOES;
}

function digitando(alvo: EventTarget | null): boolean {
  if (!(alvo instanceof HTMLElement)) {
    return false;
  }
  return alvo.isContentEditable || alvo instanceof HTMLInputElement || alvo instanceof HTMLTextAreaElement || alvo instanceof HTMLSelectElement;
}

export function Modelos(): JSX.Element {
  const { companies } = useCompany();
  const { range, selection } = usePeriod();
  const periodoTexto = describeSelection(selection);

  useEffect(() => {
    document.title = 'Modelos · Converte Fácil';
  }, []);

  // --- Filtros, ordem, visualizacao e "ver mais" ------------------------------
  const [filtros, setFiltros] = useState<FiltrosLocais>(FILTROS_INICIAIS);
  const [ordem, setOrdem] = useState<Ordem>(ORDEM_INICIAL);
  const [visualizacao, setVisualizacao] = useState<Visualizacao>('cartoes');
  const [limite, setLimite] = useState(PASSO_CARTOES);

  const alterarFiltros = useCallback(
    (parcial: Partial<FiltrosLocais>): void => {
      setFiltros((anterior) => ({ ...anterior, ...parcial }));
      setLimite(passoDe(visualizacao));
    },
    [visualizacao],
  );
  const trocarVisualizacao = (v: Visualizacao): void => {
    setVisualizacao(v);
    setLimite(passoDe(v));
  };
  const trocarOrdem = (nova: Ordem): void => {
    setOrdem(nova);
    setLimite(passoDe(visualizacao));
  };
  const ordenarPorColuna = (coluna: ColunaOrdem): void => trocarOrdem(ordem.coluna === coluna ? { coluna, desc: !ordem.desc } : ordemNatural(coluna));

  // Um id de empresa que saiu da lista volta para "todas" sem estado invalido.
  const filtrosEfetivos = useMemo<FiltrosLocais>(() => {
    const escopoValido = filtros.escopo === ESCOPO_TODAS || filtros.escopo === ESCOPO_GLOBAIS || companies.some((c) => c.id === filtros.escopo);
    return escopoValido ? filtros : { ...filtros, escopo: ESCOPO_TODAS };
  }, [filtros, companies]);
  const companyId = companyIdDoEscopo(filtrosEfetivos.escopo);
  const empresaFiltrada = companies.find((c) => c.id === companyId) ?? null;

  const buscar = useCallback((signal: AbortSignal) => fetchTemplates({ companyId, range }, signal), [companyId, range]);
  const { state, reload } = useApiResource(buscar, 60_000);

  // --- Sobreposicao local da previa -------------------------------------------
  const [locais, setLocais] = useState<Sobreposicao>(SEM_SOBREPOSICAO);
  const sequencia = useRef(0);
  const gerarId = useCallback((): string => {
    sequencia.current += 1;
    return `SIMULADO-modelo-local-${String(sequencia.current).padStart(3, '0')}`;
  }, []);
  const [aviso, avisar, limparAviso] = useAviso();

  const biblioteca = useMemo(() => (state.status === 'success' ? mesclar(state.data.items, locais) : []), [state, locais]);
  const noEscopo = useMemo(() => biblioteca.filter((t) => pertenceAoEscopo(t, filtrosEfetivos.escopo)), [biblioteca, filtrosEfetivos.escopo]);
  const ranking = useMemo(() => ranquear(noEscopo), [noEscopo]);
  const simulado = state.status === 'success' && state.simulated;
  const insights = useMemo(() => gerarInsights(noEscopo, simulado ? AGORA_SIMULADO.getTime() : Date.now()), [noEscopo, simulado]);
  const filtrados = useMemo(() => ordenar(aplicarFiltros(biblioteca, filtrosEfetivos), ordem), [biblioteca, filtrosEfetivos, ordem]);
  const visiveis = filtrados.slice(0, limite);
  const restantes = filtrados.length - visiveis.length;
  const nomesExistentes = useMemo(() => biblioteca.map((t) => t.name), [biblioteca]);

  const contagens = useMemo(
    () => ({
      tab: contarPorValor(biblioteca, filtrosEfetivos, 'tab'),
      categoria: contarPorValor(biblioteca, filtrosEfetivos, 'categoria'),
      setor: contarPorValor(biblioteca, filtrosEfetivos, 'setor'),
      status: contarPorValor(biblioteca, filtrosEfetivos, 'status'),
      publico: contarPorValor(biblioteca, filtrosEfetivos, 'publico'),
      uso: contarUso(biblioteca, filtrosEfetivos),
    }),
    [biblioteca, filtrosEfetivos],
  );

  const abas: readonly TabItem<TemplateTab>[] = ABAS.map((tab) => ({
    value: tab,
    label: ABA_LABEL[tab],
    icon: ABA_ICONE[tab],
    count: formatCount(contagens.tab.por(tab)),
  }));

  const porId = useCallback((id: string): MessageTemplate | null => biblioteca.find((t) => t.id === id) ?? null, [biblioteca]);
  const empresaDe = useCallback((t: MessageTemplate): CompanySummary | null => companies.find((c) => c.id === t.companyId) ?? null, [companies]);

  // --- Modais -------------------------------------------------------------------
  const [previa, setPrevia] = useState<{ readonly id: string; readonly aba: AbaDaPrevia } | null>(null);
  const [editor, setEditor] = useState<EditorAberto | null>(null);
  const [selecionados, setSelecionados] = useState<readonly string[]>([]);
  const [comparando, setComparando] = useState(false);
  const fecharPrevia = useCallback((): void => setPrevia(null), []);
  const fecharEditor = useCallback((): void => setEditor(null), []);
  const fecharComparacao = useCallback((): void => setComparando(false), []);

  const modeloDaPrevia = previa === null ? null : porId(previa.id);
  const selecionadosValidos = selecionados.map(porId).filter((t): t is MessageTemplate => t !== null);
  const [compA, compB] = selecionadosValidos;

  const abrirPrevia = useCallback((t: MessageTemplate, aba: AbaDaPrevia = 'previa'): void => setPrevia({ id: t.id, aba }), []);
  const abrirNovo = (): void => setEditor({ modo: 'novo', base: null });
  const editar = (t: MessageTemplate): void => {
    setPrevia(null);
    setEditor({ modo: 'editar', base: t });
  };

  const duplicar = (t: MessageTemplate): void => {
    const agora = new Date().toISOString();
    const nome = nomeDaCopia(t.name, nomesExistentes);
    const meta = canalPassaPelaMeta(t.channel);
    const semVersao: MessageTemplate = {
      ...t,
      id: gerarId(),
      name: nome,
      status: meta ? 'pendente' : 'texto_livre',
      statusNote: meta ? 'Cópia criada na prévia; precisa ser enviada à Meta para aprovação.' : null,
      usedIn: [],
      performance: DESEMPENHO_ZERO,
      previousPerformance: null,
      versions: [],
      updatedAt: agora,
    };
    const versaoOriginal = t.versions[t.versions.length - 1]?.version ?? 1;
    const copia: MessageTemplate = { ...semVersao, versions: [versaoDe(semVersao, 1, agora, `Cópia de ${t.name} (v${versaoOriginal})`)] };
    setLocais((anterior) => ({ ...anterior, criados: [...anterior.criados, copia] }));
    setPrevia(null);
    setComparando(false);
    setEditor({ modo: 'editar', base: copia, copiaDe: t.name });
    avisar(`Cópia criada na prévia: ${nome}`);
  };

  const desarquivar = useCallback(
    (id: string): void => {
      setLocais((anterior) => ({ ...anterior, arquivados: anterior.arquivados.filter((a) => a !== id) }));
      avisar('Arquivamento desfeito');
    },
    [avisar],
  );
  const arquivar = (t: MessageTemplate): void => {
    setLocais((anterior) => ({ ...anterior, arquivados: [...anterior.arquivados, t.id] }));
    setSelecionados((sel) => sel.filter((id) => id !== t.id));
    setPrevia(null);
    avisar(`${t.name} arquivado na prévia`, () => desarquivar(t.id));
  };

  const salvar = useCallback(
    (template: MessageTemplate, modo: ModoEditor, mudou: boolean): void => {
      setEditor(null);
      if (!mudou) {
        avisar('Nada mudou: o modelo continua como estava');
        return;
      }
      setLocais((anterior) =>
        modo === 'editar' ? { ...anterior, substituidos: { ...anterior.substituidos, [template.id]: template } } : { ...anterior, criados: [...anterior.criados, template] },
      );
      avisar(modo === 'editar' ? `${template.name} salvo na prévia` : `${template.name} criado na prévia`);
    },
    [avisar],
  );

  const restaurar = (t: MessageTemplate, versao: TemplateVersion): void => {
    const novo = restaurarVersao(t, versao, new Date().toISOString());
    setLocais((anterior) => ({ ...anterior, substituidos: { ...anterior.substituidos, [t.id]: novo } }));
    avisar(`${t.name}: restaurado da v${versao.version} na prévia`, () => {
      setLocais((anterior) => ({ ...anterior, substituidos: { ...anterior.substituidos, [t.id]: t } }));
      avisar('Restauração desfeita');
    });
  };

  const alternarComparacao = (t: MessageTemplate): void => {
    setSelecionados((sel) => (sel.includes(t.id) ? sel.filter((id) => id !== t.id) : sel.length >= 2 ? sel : [...sel, t.id]));
  };

  const rolarParaLista = (): void => {
    document.getElementById(ID_LISTA)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const executarAcao = (acao: AcaoInsight): void => {
    switch (acao.kind) {
      case 'editar': {
        const t = porId(acao.id);
        if (t !== null) {
          editar(t);
        }
        return;
      }
      case 'previa': {
        const t = porId(acao.id);
        if (t !== null) {
          abrirPrevia(t, acao.aba);
        }
        return;
      }
      case 'comparar':
        setSelecionados([...acao.ids]);
        setComparando(true);
        return;
      case 'filtrar':
        alterarFiltros(acao.filtros);
        rolarParaLista();
        return;
    }
  };

  // --- Atalho "/" para a busca -----------------------------------------------------
  const algumModalAberto = modeloDaPrevia !== null || editor !== null || (comparando && compA !== undefined && compB !== undefined);
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey || algumModalAberto || digitando(e.target)) {
        return;
      }
      const campo = document.getElementById(ID_BUSCA);
      if (campo instanceof HTMLInputElement) {
        e.preventDefault();
        campo.focus();
        campo.select();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [algumModalAberto]);

  const escopoTexto =
    filtrosEfetivos.escopo === ESCOPO_TODAS ? 'Todas as empresas' : filtrosEfetivos.escopo === ESCOPO_GLOBAIS ? 'Só modelos globais' : empresaFiltrada?.name ?? 'Todas as empresas';
  const filtrosAtivos = contarFiltrosFinos(filtrosEfetivos);
  const limparFiltrosFinos = (): void => alterarFiltros(FILTROS_FINOS_ZERADOS);
  const atualizando = state.status === 'success' && state.refreshing;
  const cartoes = (lista: readonly MessageTemplate[], classe: string): JSX.Element => (
    <ul className={classe}>
      {lista.map((t) => (
        <CartaoModelo
          key={t.id}
          template={t}
          empresa={empresaDe(t)}
          posicao={ranking.get(t.id) ?? null}
          selecionado={selecionados.includes(t.id)}
          podeSelecionar={selecionados.length < 2}
          onAbrir={abrirPrevia}
          onComparar={alternarComparacao}
        />
      ))}
    </ul>
  );

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="modelo"
        title="Modelos"
        badges={
          <>
            <Badge tone="brand" dot>
              Administração
            </Badge>
            <Badge tone="neutral" icon="escudo">
              Só depois do fato
            </Badge>
          </>
        }
        description="Templates de WhatsApp, e-mail e voz da régua e dos disparos, com o resultado de cada um. Todos falam com quem já abandonou o checkout ou já teve o plano vencido; quem responde cai com a vendedora e a régua para."
        aside={
          <>
            <RefreshingDot active={atualizando} />
            <Badge tone="neutral" icon="calendario">
              {periodoTexto}
            </Badge>
            <Button
              variant="primary"
              icon="mais"
              onClick={abrirNovo}
              disabled={state.status !== 'success'}
              title={state.status === 'success' ? undefined : 'Disponível assim que a biblioteca carregar'}
            >
              Novo modelo
            </Button>
          </>
        }
      />

      {state.status === 'loading' ? <EsqueletoModelos /> : null}

      {state.status === 'error' ? <ErrorPanel title="Não foi possível carregar os modelos" error={state.error} onRetry={reload} /> : null}

      {state.status === 'success' ? (
        <>
          {state.simulated ? (
            <SimulatedNotice>
              Biblioteca e números fictícios e fixos, escritos para a prévia. Criar, editar, duplicar, arquivar e restaurar versões funcionam só nesta tela e somem ao recarregar a
              página.
            </SimulatedNotice>
          ) : null}

          <ResumoBiblioteca lista={noEscopo} />

          <div className="grid gap-6 lg:grid-cols-3">
            <OQueFazerModelos insights={insights} onAcao={executarAcao} className="lg:col-span-2" />
            <RankingModelos lista={noEscopo} ranking={ranking} onAbrir={(t) => abrirPrevia(t, 'desempenho')} />
          </div>

          <section id={ID_LISTA} aria-labelledby="titulo-biblioteca" className="flex scroll-mt-6 flex-col gap-4">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <h2 id="titulo-biblioteca" className="text-base font-semibold text-ink">
                  Biblioteca de modelos
                </h2>
                <p className="text-sm text-ink-muted">
                  {contar(noEscopo.length, 'modelo', 'modelos')} · {escopoTexto}. Filtros combinam entre si; o número em cada opção é quanto sobraria ao clicar.
                </p>
              </div>
            </div>

            <Tabs label="Canal do modelo" items={abas} value={filtrosEfetivos.tab} onChange={(tab) => alterarFiltros({ tab })} panelId={ID_PAINEL} />

            <FiltrosDeModelos
              filtros={filtrosEfetivos}
              onChange={alterarFiltros}
              companies={companies}
              contagens={contagens}
              ordem={ordem}
              onOrdem={trocarOrdem}
              visualizacao={visualizacao}
              onVisualizacao={trocarVisualizacao}
              filtrosAtivos={filtrosAtivos}
              onLimpar={limparFiltrosFinos}
              idBusca={ID_BUSCA}
            />

            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-ink-muted" aria-live="polite">
                <span className="font-medium text-ink">{contar(filtrados.length, 'modelo', 'modelos')}</span> em {ABA_LABEL[filtrosEfetivos.tab]}
                {filtrados.length > visiveis.length ? ` · mostrando ${formatCount(visiveis.length)}` : ''}
              </p>
              <span role="status" aria-live="polite" className="flex items-center gap-2">
                {aviso === null ? null : (
                  <>
                    <Badge tone="good" dot icon="check">
                      {aviso.texto}
                    </Badge>
                    {aviso.desfazer === undefined ? null : (
                      <Button
                        variant="ghost"
                        size="sm"
                        icon="atualizar"
                        onClick={() => {
                          const desfazer = aviso.desfazer;
                          limparAviso();
                          desfazer?.();
                        }}
                      >
                        Desfazer
                      </Button>
                    )}
                  </>
                )}
              </span>
            </div>

            {selecionados.length > 0 ? (
              <Panel tone="brand" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <p className="flex min-w-0 items-center gap-2 text-sm text-ink">
                  <Icon name="copiar" size="sm" className="shrink-0 text-brand" />
                  <span className="min-w-0">
                    <span className="font-semibold">Comparar lado a lado:</span>{' '}
                    <span className="font-mono">{selecionadosValidos.map((t) => t.name).join(' × ')}</span>
                    {selecionadosValidos.length < 2 ? <span className="text-ink-muted"> · escolha mais um</span> : null}
                  </span>
                </p>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setSelecionados([])}>
                    Limpar
                  </Button>
                  <Button variant="primary" size="sm" icon="olho" disabled={selecionadosValidos.length < 2} onClick={() => setComparando(true)}>
                    Comparar
                  </Button>
                </div>
              </Panel>
            ) : null}

            <div id={ID_PAINEL} role="tabpanel" aria-label={`Modelos de ${ABA_LABEL[filtrosEfetivos.tab]}`}>
              {filtrados.length === 0 ? (
                <EmptyState
                  title={filtrosEfetivos.busca.trim() !== '' ? `Nenhum modelo com “${filtrosEfetivos.busca.trim()}”` : 'Nenhum modelo com esses filtros'}
                  description={
                    filtrosAtivos > 0
                      ? `A busca olha nome, texto, assunto, botões, público e onde o modelo está ligado. Afrouxe um filtro — as outras abas podem ter resultado (${ABAS.filter((t) => t !== filtrosEfetivos.tab)
                          .map((t) => `${ABA_LABEL[t]}: ${formatCount(contagens.tab.por(t))}`)
                          .join(' · ')}).`
                      : `Ainda não há modelo de ${ABA_LABEL[filtrosEfetivos.tab]} neste escopo (${escopoTexto}). Crie o primeiro: ele nasce falando com quem já abandonou ou já venceu.`
                  }
                  action={
                    filtrosAtivos > 0 ? (
                      <Button variant="secondary" icon="filtro" onClick={limparFiltrosFinos}>
                        Limpar filtros
                      </Button>
                    ) : (
                      <Button variant="secondary" icon="mais" onClick={abrirNovo}>
                        Novo modelo
                      </Button>
                    )
                  }
                />
              ) : visualizacao === 'tabela' ? (
                <>
                  <div className="hidden md:block">
                    <TabelaModelos
                      itens={visiveis}
                      empresaDe={empresaDe}
                      ranking={ranking}
                      ordem={ordem}
                      onOrdenar={ordenarPorColuna}
                      selecionados={selecionados}
                      podeSelecionarMais={selecionados.length < 2}
                      onAbrir={abrirPrevia}
                      onComparar={alternarComparacao}
                    />
                  </div>
                  <div className="md:hidden">{cartoes(visiveis, 'grid gap-4')}</div>
                </>
              ) : (
                cartoes(visiveis, 'grid gap-4 md:grid-cols-2 xl:grid-cols-3')
              )}

              {restantes > 0 ? (
                <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
                  <Button variant="secondary" icon="chevron" onClick={() => setLimite((l) => l + passoDe(visualizacao))}>
                    Ver mais {formatCount(Math.min(restantes, passoDe(visualizacao)))}
                  </Button>
                  <span className="text-xs text-ink-subtle">
                    {formatCount(visiveis.length)} de {formatCount(filtrados.length)} · {formatCount(restantes)} restantes
                  </span>
                </div>
              ) : null}
            </div>
          </section>

          <div className="flex flex-col gap-2">
            <Footnote>
              Todo modelo fala com quem <span className="font-medium text-ink">já abandonou o checkout</span> (30 min sem pagar) ou <span className="font-medium text-ink">já teve o plano vencido</span>. Não
              existe lembrete antes do fato: sem ele não há recuperação e não há comissão.
            </Footnote>
            <Footnote>
              Botão de resposta (“Tenho uma dúvida”) ou resposta digitada caem com a <span className="font-medium text-ink">vendedora do comercial</span> e param a régua para aquele lead. Se ela
              fechar em até 7 dias, a comissão continua do toque que trouxe o lead (ADR-001 E5); a vendedora aparece como “fechado por”.
            </Footnote>
            <Footnote>
              <span className="font-medium text-ink">Utility</span> são os da régua (carrinho parado, acesso vencido, PIX expirado). <span className="font-medium text-ink">Marketing</span> exige
              opt-in e passa por análise mais rígida da Meta. Fora da janela de 24 h, a API oficial só entrega modelo aprovado; texto livre pela Evolution não tem botões; o roteiro de voz
              respeita o teto de 2 ligações por lead.
            </Footnote>
          </div>

          <p className="text-xs text-ink-subtle">
            Números de {formatDate(state.data.periodStart)} a {formatDate(state.data.periodEnd)} ({periodoTexto})
            {state.data.previousPeriod === null
              ? ' · sem período anterior para comparar'
              : ` · comparados com ${formatDate(state.data.previousPeriod.start)} a ${formatDate(state.data.previousPeriod.end)}, o período anterior de mesmo tamanho`}{' '}
            · {contar(biblioteca.length, 'modelo na biblioteca', 'modelos na biblioteca')} · {escopoTexto}
          </p>
        </>
      ) : null}

      {modeloDaPrevia !== null && previa !== null ? (
        <ModalPreviaModelo
          key={modeloDaPrevia.id}
          template={modeloDaPrevia}
          empresa={empresaDe(modeloDaPrevia)}
          posicao={ranking.get(modeloDaPrevia.id) ?? null}
          periodo={periodoTexto}
          abaInicial={previa.aba}
          onClose={fecharPrevia}
          onDuplicar={duplicar}
          onEditar={editar}
          onArquivar={arquivar}
          onRestaurar={restaurar}
        />
      ) : null}

      {comparando && compA !== undefined && compB !== undefined ? (
        <ModalCompararModelos a={compA} b={compB} empresaDe={empresaDe} ranking={ranking} periodo={periodoTexto} onClose={fecharComparacao} onDuplicar={duplicar} />
      ) : null}

      <ModalEditorModelo
        aberto={editor}
        companies={companies}
        empresaPadrao={companyId}
        nomesExistentes={nomesExistentes}
        gerarId={gerarId}
        onCancelar={fecharEditor}
        onSalvar={salvar}
      />
    </section>
  );
}
